import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Space, Table, message } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { type TeamMember } from "@fitness-app/data-models/entities/TeamMember";

import { integrationApi } from "~/store/initializeStore";
import { teamMemberBadgeStatus } from "../salesCrmConstants";

type SalesSellersTabProps = {
  sellers: TeamMember[];
  loading: boolean;
};

export const SalesSellersTab = ({ sellers, loading }: SalesSellersTabProps): React.ReactElement => {
  const { t } = useTranslation("team");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const syncSellersMutation = useMutation({
    mutationFn: async () => integrationApi.post("/calendly/sales-call/sync-sales-reps"),
    onSuccess: async ({ data }) => {
      await queryClient.invalidateQueries({ queryKey: ["salesCrmSellers"] });
      void message.success(
        `Sync Calendly: ${data.matched?.length ?? 0} dopasowanych, ${data.unmatched?.length ?? 0} nowych`,
      );
    },
    onError: (error) => {
      void message.error(`Synchronizacja z Calendly nie powiodła się: ${(error as Error).message}`);
    },
  });

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={16}>
      <Space>
        <Button type="primary" onClick={() => navigate("/team")}>
          Dodaj ręcznie
        </Button>
        <Button loading={syncSellersMutation.isLoading} onClick={() => syncSellersMutation.mutate()}>
          Synchronizuj Calendly
        </Button>
      </Space>
      <Table<TeamMember> rowKey="id" dataSource={sellers} loading={loading}>
        <Table.Column<TeamMember> title="Imię i nazwisko" render={(_, row) => `${row.firstName} ${row.lastName}`} />
        <Table.Column<TeamMember> title="Email" dataIndex="email" />
        <Table.Column<TeamMember> title="Calendly" dataIndex="calendlyEmail" render={(value) => value ?? "-"} />
        <Table.Column<TeamMember>
          title="Status"
          dataIndex="status"
          render={(value: TeamMember["status"]) => (
            <Badge status={teamMemberBadgeStatus[value]} text={t(`status.${value.toLowerCase()}`)} />
          )}
        />
      </Table>
    </Space>
  );
};
